import { Divider, Form } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { UnitType } from 'entities/Contract';
import { useGetAllDiscountsQuery } from 'entities/Discount';
import { InvoiceFrequencyType } from 'entities/Invoice';
import {
  AccountTypeSetting,
  LoyaltyUserSetting,
  PaymentFrequencySetting,
  RentOptionDocument,
  useCreateRentOptionMutation,
  useUpdateRentOptionMutation,
} from 'entities/RentOption';
import { UserPermissions } from 'entities/User';
import { useGetAllWarehousesQuery } from 'entities/Warehouse';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'shared/ui/Button';
import { Checkbox } from 'shared/ui/Checkbox';
import { InfoTag } from 'shared/ui/InfoTag';
import { InputNumber } from 'shared/ui/InputNumber';
import { MultipleSelect } from 'shared/ui/MultipleSelect';
import { Select } from 'shared/ui/Select';
import { TextArea } from 'shared/ui/TextArea';
import { validateLocalizedJSON } from 'shared/utils/helpers/JSONLocalization';
import { transformEmptyStringToNull } from 'shared/utils/helpers/transformEmptyStringToNull';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { useDefaultLocalizedDescription } from 'shared/utils/hooks/useDefaultLocalizedDescription';
import { POSITIVE_INTEGER_REGEX } from 'shared/utils/regex';
import { DrawerMode } from '../model/types';

interface RentOptionFormProps {
  rentOption?: Nullable<RentOptionDocument>;
  mode: Nullable<DrawerMode>;
  handleClose: () => void;
}

export const RentOptionForm: FC<RentOptionFormProps> = memo((props) => {
  const { rentOption, mode, handleClose } = props;
  const { t } = useAppTranslation(['contracts', 'common']);

  const [form] = Form.useForm();

  const [selectedDiscountId, setSelectedDiscountId] = useState<Nullable<string>>(null);

  const { data: discounts } = useGetAllDiscountsQuery();
  const { data: warehouses } = useGetAllWarehousesQuery();
  const [createRentOption, { isLoading: isCreationLoading }] = useCreateRentOptionMutation();
  const [updateRentOption, { isLoading: isUpdationLoading }] = useUpdateRentOptionMutation();

  const isLoading = isUpdationLoading || isCreationLoading;

  const isForbidden = useCheckPermission(UserPermissions.EDIT_UNITS);

  const defaultLocalizedDescription = useDefaultLocalizedDescription();

  const warehouseOptions = useMemo(() => {
    return (
      warehouses?.map((warehouse) => ({
        label: warehouse.name,
        value: warehouse.warehouseId,
      })) || []
    );
  }, [warehouses]);

  useEffect(() => {
    if (rentOption) {
      form.setFieldsValue(rentOption);
    } else {
      form.resetFields();
    }
  }, [form, rentOption]);

  useEffect(() => {
    const discount = discounts?.find((discount) => discount.discountId === selectedDiscountId);

    form.setFieldsValue({
      unitSizes: discount?.unitSizes || null,
      paymentFrequencySetting: discount?.paymentFrequency || null,
      accountTypeSetting: discount?.accountType || null,
      loyaltyUserSetting: discount?.loyaltyPeriod || null,
      invoiceFrequencyType: discount?.periodType || null,
    });
  }, [discounts, form, selectedDiscountId]);

  useEffect(() => {
    if (rentOption?.discountKeys?.length && discounts?.length) {
      const matchedDiscount = discounts.find((discount) => discount.discountKeys.some((key) => rentOption?.discountKeys?.includes(key)));

      if (matchedDiscount) {
        setSelectedDiscountId(matchedDiscount.discountId);
      }
    }

    return () => {
      setSelectedDiscountId(null);
    };
  }, [rentOption, discounts]);

  const handleSubmit = useCallback(
    async (data: CustomAny): Promise<void> => {
      const discount = discounts?.find((discount) => discount.discountId === selectedDiscountId);

      const rentOptionData = {
        ...transformEmptyStringToNull(data),
        warehouseIds: data.warehouseIds?.length ? data.warehouseIds : null,
        discountKeys: discount?.discountKeys || null,
      };

      try {
        switch (mode) {
          case DrawerMode.RENT_OPTION_CREATE:
            await createRentOption(rentOptionData as RentOptionDocument).unwrap();
            showNotification('info', t('Success', { ns: 'common' }), t('Rent option has been successfully created'));
            break;
          case DrawerMode.RENT_OPTION_EDIT:
            if (rentOption) {
              const updateRentOptionDto: RentOptionDocument = {
                ...rentOptionData,
                rentOptionId: rentOption.rentOptionId,
              };

              await updateRentOption(updateRentOptionDto).unwrap();
              showNotification('info', t('Success', { ns: 'common' }), t('Rent option has been successfully updated'));
            }
            break;
        }

        form.resetFields();

        handleClose();
      } catch (error: CustomAny) {
        console.log(error);
        showNotification(
          'error',
          t('Error', { ns: 'common' }),
          t(`Error when ${mode === DrawerMode.RENT_OPTION_CREATE ? 'creating' : 'updating'} rent option`),
        );
      }
    },
    [discounts, selectedDiscountId, mode, form, handleClose, createRentOption, t, rentOption, updateRentOption],
  );

  const loyaltyPeriodOptions = Object.values(LoyaltyUserSetting).map((option) => ({
    label: t(option),
    value: option,
  }));

  const accountTypeOptions = Object.values(AccountTypeSetting).map((option) => ({
    label: t(option),
    value: option,
  }));

  const paymentFrequencyOptions = Object.values(PaymentFrequencySetting).map((option) => ({
    label: t(option),
    value: option,
  }));

  const invoiceFrequencyTypeOptions = Object.values(InvoiceFrequencyType).map((option) => ({
    label: t(option),
    value: option,
  }));

  const unitTypeOptions = useMemo(() => {
    return [
      {
        label: t('All'),
        value: 'All',
      },
      {
        label: t('Box'),
        value: UnitType.BOX,
      },
      {
        label: t('Office'),
        value: UnitType.OFFICE,
      },
      {
        label: t('Insurance'),
        value: UnitType.INSURANCE,
      },
      {
        label: t('Parking place'),
        value: UnitType.PARKING_PLACE,
      },
      {
        label: t('Postal box'),
        value: UnitType.POSTAL_BOX,
      },
      {
        label: t('Renting stuff'),
        value: UnitType.RENTING_STUFF,
      },
      {
        label: t('Deposit'),
        value: UnitType.DEPOSIT,
      },
    ];
  }, [t]);

  const discountKeyOptions = useMemo(() => {
    const discountKeyOptions = discounts?.map((discount) => {
      return {
        label: `${discount.name} (${discount.discountKeys[0]})`,
        value: discount.discountId,
      };
    });

    return discountKeyOptions || [];
  }, [discounts]);

  const discountContractDurationOptions = useMemo(() => {
    const discount = discounts?.find((discount) => discount.discountId === selectedDiscountId);

    const discountDurationOptions = discount?.contractDurations?.map((duration) => {
      return {
        label: duration,
        value: Number(duration),
      };
    });

    return discountDurationOptions || [];
  }, [discounts, selectedDiscountId]);

  const changeDiscount = useCallback((discountId: string): void => {
    setSelectedDiscountId(discountId);
  }, []);

  return (
    <div className="min-h-full h-max flex">
      <div className="flex-1 shadow-left">
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="flex flex-col px-10 py-4" disabled={isForbidden}>
          <div className="flex justify-between items-center pt-4 pb-10">
            <div className="font-semibold text-3xl">{t(`${mode === DrawerMode.RENT_OPTION_EDIT ? 'Edit' : 'Create'} rent option`)}</div>
            <div className="text-primaryLight font-normal cursor-pointer" onClick={handleClose}>
              {t('Close', { ns: 'common' })}
            </div>
          </div>
          <Form.Item
            label={t('Label')}
            name="label"
            rules={[
              {
                required: true,
                message: t('Please, enter label!'),
              },
              () => ({
                async validator(_, value) {
                  const isValidDescription = validateLocalizedJSON(value);

                  if (!value || isValidDescription) {
                    await Promise.resolve();
                    return;
                  }
                  return await Promise.reject(new Error(t('Label must be a valid localized JSON')));
                },
              }),
            ]}
          >
            <TextArea placeholder={t('Add label...')} bordered rows={3} defaultValue={defaultLocalizedDescription} />
          </Form.Item>
          <Form.Item label={t('Warehouse')} name="warehouseIds">
            <MultipleSelect className="w-full" placeholder={t('All')} bordered options={warehouseOptions} allowClear />
          </Form.Item>

          <InfoTag>
            {t(
              'By selecting a discount key, you link the rent option with the discount and apply the discount parameters. To change the parameters of the rent option, update the discount settings — they will automatically be applied to the rent option.',
            )}
          </InfoTag>

          <Form.Item name="discountKeys" label={t('Discount key')}>
            <Select options={discountKeyOptions} placeholder={t('Please, select discount')} bordered onChange={changeDiscount} allowClear />
          </Form.Item>

          <div className="flex gap-4">
            <Form.Item
              label={t('Unit type')}
              name="unitTypeSetting"
              rules={[{ required: true, message: `${t('Please, select unit type')}!` }]}
              className="flex-1"
            >
              <Select options={unitTypeOptions} placeholder={t('Select unit type')} bordered />
            </Form.Item>

            <Form.Item
              name="minContractDuration"
              label={t('Min contract duration')}
              rules={[
                { required: true, message: `${t('Please, enter min contract duration')}!` },
                { pattern: POSITIVE_INTEGER_REGEX, message: t('Please provide positive integer!') },
              ]}
            >
              {discountContractDurationOptions.length ? (
                <Select options={discountContractDurationOptions} placeholder={t('Min contract duration')} bordered />
              ) : (
                <InputNumber placeholder={t('Min contract duration')} bordered />
              )}
            </Form.Item>
          </div>

          <Form.Item name="unitSizes">
            <TextArea
              placeholder={t('All')}
              bordered
              tooltip={t('Unit sizes will be populated based on the selected discount, they must be identical!')}
              label={t('Unit sizes')}
              readOnly
              rows={4}
              isDisabled={Boolean(selectedDiscountId)}
            />
          </Form.Item>

          <Divider className="mb-6 mt-0" />

          <div className="flex gap-4">
            <Form.Item
              name="loyaltyUserSetting"
              label={t('Client loyalty')}
              rules={[{ required: true, message: `${t('Please, select client loyalty')}!` }]}
              className="flex-1"
            >
              <Select
                options={loyaltyPeriodOptions}
                placeholder={t('Select client loyalty')}
                bordered
                disabled={Boolean(selectedDiscountId)}
              />
            </Form.Item>

            <Form.Item
              name="accountTypeSetting"
              label={t('Account type')}
              rules={[{ required: true, message: `${t('Please, select account type')}!` }]}
              className="flex-1"
            >
              <Select options={accountTypeOptions} placeholder={t('Select account type')} bordered disabled={Boolean(selectedDiscountId)} />
            </Form.Item>
          </div>

          <div className="flex gap-4">
            <Form.Item
              name="paymentFrequencySetting"
              label={t('Payment frequency')}
              rules={[{ required: true, message: `${t('Please, select payment frequency')}!` }]}
              className="flex-1 w-1/2"
            >
              <Select
                options={paymentFrequencyOptions}
                placeholder={t('Payment frequency')}
                bordered
                disabled={Boolean(selectedDiscountId)}
              />
            </Form.Item>

            <Form.Item
              name="invoiceFrequencyType"
              label={t('Period type')}
              rules={[{ required: true, message: `${t('Please, select period type')}!` }]}
              className="flex-1 w-1/2"
            >
              <Select
                options={invoiceFrequencyTypeOptions}
                placeholder={t('Select period type')}
                bordered
                disabled={Boolean(selectedDiscountId)}
              />
            </Form.Item>
          </div>

          <Form.Item name="order" rules={[{ pattern: POSITIVE_INTEGER_REGEX, message: t('Please provide positive integer!') }]}>
            <InputNumber
              placeholder={t('Example: 1')}
              bordered
              tooltip={t('Priority of displaying the rental option on the booking')}
              label={t('Order')}
            />
          </Form.Item>

          <Form.Item name="isSpecialPromotion" valuePropName="checked">
            <Checkbox name="isSpecialPromotion">{t('Special Promotion')}</Checkbox>
          </Form.Item>

          <Form.Item name="showAsPromotion" valuePropName="checked">
            <Checkbox name="showAsPromotion">{t('Show as Promotion')}</Checkbox>
          </Form.Item>

          <Divider className="mb-6 mt-0" />
          <Form.Item
            label={t('Description')}
            name="description"
            rules={[
              () => ({
                async validator(_, value) {
                  const isValidDescription = validateLocalizedJSON(value);

                  if (!value || isValidDescription) {
                    await Promise.resolve();
                    return;
                  }
                  return await Promise.reject(new Error(t('Description must be a valid localized JSON')));
                },
              }),
            ]}
          >
            <TextArea placeholder={t('Add description...')} bordered rows={3} defaultValue={defaultLocalizedDescription} />
          </Form.Item>

          <Form.Item
            label={t('Extended description')}
            name="extendedDescription"
            rules={[
              () => ({
                async validator(_, value) {
                  const isValidDescription = validateLocalizedJSON(value);

                  if (!value || isValidDescription) {
                    await Promise.resolve();
                    return;
                  }
                  return await Promise.reject(new Error(t('Extended description must be a valid localized JSON')));
                },
              }),
            ]}
          >
            <TextArea placeholder={t('Add description...')} bordered rows={3} defaultValue={defaultLocalizedDescription} />
          </Form.Item>

          <Button type="submit" isLoading={isLoading} isDisabled={isForbidden}>
            {t('Save', { ns: 'common' })}
          </Button>
        </Form>
      </div>
    </div>
  );
});
